import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { InventoryItem } from '../../../shared/CharacterTypes';
import { ITEM_DATABASE } from '../../../shared/content/generated/items.gen';
import { boundItemLabel, isBindingBound } from '../../../shared/inventory/itemBinding';
import { ItemGrid, type ItemData } from '../components/ItemKit';
import { TomeButton } from '../primitives/tome';
import { useInventory, type InventorySortBy } from '../hooks/useInventory';
import { DEFAULT_BAG_SLOTS } from '../../../shared/inventory/bagCapacity';
import { uiActionBridge } from '../framework/uiActionBridge';
import { useItemTooltipViewer } from '../hooks/useItemTooltipViewer';
import { formatGold } from '../format';
import { InventoryItemDetail } from './Inventory/InventoryItemDetail';
import { INVENTORY_TABS, inventoryItemData, inventoryItemName, inventoryRows, matchesInventoryTab, type InventoryTab } from './Inventory/InventoryPresentation';
import './Inventory/InventoryPresentation.css';

/** Bag-only inventory snapshot; equipped slots remain the character sheet's authority. */
export const InventoryContent: React.FC = () => {
  const [activeTab, setActiveTab] = useState<InventoryTab>('All');
  const [selectedKey, setSelectedKey] = useState<string>();
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState<InventorySortBy>('type');
  const rootRef = useRef<HTMLElement | null>(null);
  const dropAcceptedRef = useRef(false);
  const viewer = useItemTooltipViewer();
  const { items, gold, sort, maxSlots = DEFAULT_BAG_SLOTS } = useInventory();
  const allRows = useMemo(() => inventoryRows(items), [items]);
  const filteredRows = useMemo(() => allRows.filter(({ item }) => matchesInventoryTab(item, activeTab)
    && `${inventoryItemName(item)} ${item.id}`.toLowerCase().includes(query.trim().toLowerCase())), [allRows, activeTab, query]);
  // Every slot, empty ones included, on the unfiltered bag (plan 06); never fewer
  // cells than real rows when a legacy bag is over capacity.
  const showAllSlots = activeTab === 'All' && !query.trim();
  const usedSlots = allRows.length;
  const freeSlots = Math.max(0, maxSlots - usedSlots);
  const filteredItems = filteredRows.map(({ item }) => item);
  const selectedIndex = filteredRows.findIndex(row => row.key === selectedKey);
  const selectedItem = selectedIndex >= 0 ? filteredItems[selectedIndex] : null;
  const activate = (item: InventoryItem) => uiActionBridge.dispatch('useItem', { itemId: item.id });
  const drop = (item: InventoryItem, quantity: number) => uiActionBridge.dispatch('dropItem', { itemId: item.id, quantity });
  const select = (_item: ItemData | null, index: number) => setSelectedKey(filteredRows[index]?.key);
  const rightClick = (item: ItemData | null, index: number) => {
    select(item, index);
    if (filteredItems[index]) activate(filteredItems[index]);
  };
  useEffect(() => {
    const onAcceptedDrop = () => { dropAcceptedRef.current = true; };
    window.addEventListener('helix:action-drag-end', onAcceptedDrop);
    return () => window.removeEventListener('helix:action-drag-end', onAcceptedDrop);
  }, []);
  const dragStart = (event: React.DragEvent, item: ItemData, index: number) => {
    dropAcceptedRef.current = false;
    setSelectedKey(filteredRows[index]?.key);
    event.dataTransfer.setData('text/plain', `item:${item.id}`);
    event.dataTransfer.setData('application/x-helix-item', item.id);
    event.dataTransfer.effectAllowed = 'copy';
    window.dispatchEvent(new CustomEvent('helix:action-drag-start', { detail: { type: 'item', itemId: item.id } }));
  };
  const dragEnd = (event: React.DragEvent, _item: ItemData, index: number) => {
    const wasAccepted = dropAcceptedRef.current;
    dropAcceptedRef.current = false;
    window.dispatchEvent(new CustomEvent('helix:action-drag-end'));
    if (wasAccepted) return;
    const rect = rootRef.current?.getBoundingClientRect();
    if (rect && rect.width > 0 && rect.height > 0) {
      const inside = event.clientX >= rect.left && event.clientX <= rect.right
        && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (inside) return;
    }
    const rowItem = filteredRows[index]?.item;
    if (!rowItem) return;
    const binding = ITEM_DATABASE[rowItem.id]?.binding;
    if (binding && isBindingBound(binding)) {
      try { (globalThis as any).__errorToast?.('info', `${boundItemLabel(binding)}. It cannot be dropped.`); } catch { /* optional */ }
      return;
    }
    drop(rowItem, rowItem.quantity);
  };
  return <section ref={rootRef} className="inventory-browser" aria-label="Inventory bag"
    onDragOver={event => event.preventDefault()} onDrop={() => { dropAcceptedRef.current = true; }}>
    <header className="inventory-browser__heading"><div><p className="inventory-browser__eyebrow">Your belongings</p>
      <h2>Inventory</h2></div><span data-testid="inventory-capacity">
        {`${usedSlots} / ${maxSlots} slots${showAllSlots ? '' : ` · ${freeSlots} free`}`}</span></header>
    <div className="inventory-browser__tabs" role="tablist" aria-label="Bag categories">
      {INVENTORY_TABS.map(tab => <button key={tab} type="button" role="tab" aria-selected={activeTab === tab}
        onClick={() => { setActiveTab(tab); setSelectedKey(undefined); }}>{tab}</button>)}
    </div>
    <label className="inventory-browser__search">Search bag<input type="search" value={query}
      onChange={event => setQuery(event.target.value)} placeholder="Find an item…" /></label>
    <div className="inventory-browser__body">
      <div className="inventory-browser__collection" tabIndex={0} aria-label="Bag items">
        {filteredItems.length || showAllSlots ? <ItemGrid items={filteredItems.map(inventoryItemData)} columns={4} slotSize="md"
          maxSlots={showAllSlots ? Math.max(maxSlots, filteredItems.length) : filteredItems.length} selectedIndex={selectedIndex >= 0 ? selectedIndex : undefined}
          viewer={viewer}
          onSlotClick={select} onSlotRightClick={rightClick} onSlotDragStart={dragStart} onSlotDragEnd={dragEnd} />
          : <div className="inventory-browser__empty"><h3>{query ? 'No matching items' : 'Nothing here yet'}</h3>
            <p>Try another category or search your whole bag.</p></div>}
      </div>
      <InventoryItemDetail item={selectedItem} viewer={viewer} activate={activate} drop={drop} />
    </div>
    <footer className="inventory-browser__footer"><strong>{formatGold(gold)}</strong><div>
      <label>Sort by<select value={sortBy} onChange={event => setSortBy(event.target.value as InventorySortBy)}>
        <option value="type">Type</option><option value="name">Name</option><option value="rarity">Rarity</option>
      </select></label><TomeButton tone="secondary" onClick={() => sort(sortBy)}>Sort</TomeButton>
    </div></footer>
  </section>;
};
